/**
 * AttributionCoverage — ADR-183 Phase 5b: how much of Mission Control's own
 * recorded work says which model produced it, and how much cannot.
 *
 * WHY THIS PANEL EXISTS RATHER THAN A WRITER. Phase 5b asked for writers on
 * `messages`, `chat_job_steps` and `diagnostic_investigations`. The model that
 * produces the content in all three runs on the FLEET side — a chat reply is a
 * delegation, a job step is an agent or command task, an investigation comes from
 * a diagnostician's BlockedOnEvidence — and `DelegateReply` carries no model. So
 * MC has nothing to measure, and a writer would have to fill those columns from
 * configuration, which is a fabricated measurement.
 *
 * Reporting the gap is the honest alternative, and it is worth a surface: without
 * one, "MC attribution shipped" reads as done while three of five tables are
 * blank, and nobody comparing two models knows how much of the window they cannot
 * see.
 *
 * THREE DIFFERENT EMPTY THINGS, RENDERED DIFFERENTLY ON PURPOSE:
 *
 *   no Postgres          → "not recorded here", not a report of zero
 *   a read that failed   → an error, not an empty table
 *   rows with no model   → 0% WITH the reason attached
 *
 * and a table with no rows at all renders "—", never 0% and never 100%.
 */
import { useCallback, useEffect, useState } from 'react'

import { api, type AttributionCoverage as Coverage } from '../api/client'

/** Human label for a table, so the panel reads as work rather than as schema. */
const TABLE_LABELS: Record<string, string> = {
  authoring_runs: 'Authoring runs',
  authoring_run_attempts: 'Authoring attempts',
  messages: 'Chat messages',
  chat_job_steps: 'Job steps',
  diagnostic_investigations: 'Investigations',
}

function label(table: string): string {
  return TABLE_LABELS[table] ?? table
}

export function AttributionCoveragePanel({ hours = 24 }: { hours?: number }) {
  const [cov, setCov] = useState<Coverage | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'not_configured'>('loading')
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    try {
      const r = await api.attributionCoverage(hours)
      if (r.not_configured) { setState('not_configured'); return }
      setCov(r.coverage)
      setState('ready')
    } catch (e) {
      // NOT an empty report. A read that failed and a fleet that attributed
      // nothing look identical if both render as zeroes, and only one of them is
      // worth acting on.
      setErr(e instanceof Error ? e.message : 'attribution coverage could not be read')
      setState('error')
    }
  }, [hours])

  useEffect(() => { void load() }, [load])

  if (state === 'loading') {
    return <p className="text-[11px] text-gray-500">Reading attribution coverage…</p>
  }
  if (state === 'not_configured') {
    return (
      <p className="text-[11px] text-gray-500">
        This Mission Control has no database, so it records no model attribution to report on.
      </p>
    )
  }
  if (state === 'error') {
    return (
      <p className="text-[11px] text-amber-700">
        Attribution coverage could not be read: {err}
      </p>
    )
  }
  if (!cov) return null

  return (
    <section className="rounded border border-gray-200 bg-white p-3">
      <h2 className="text-[13px] font-semibold text-gray-900">
        Model attribution — last {Math.round(cov.window_hours)}h
      </h2>
      <p className="mt-0.5 text-[11px] text-gray-600">
        How much of what Mission Control recorded says which model produced it. A table with
        no writer reports 0% and says why — the model ran on the fleet side, where it is
        already recorded.
      </p>

      <table className="mt-2 w-full text-[11px]">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-1 pr-2 font-medium">What</th>
            <th className="py-1 pr-2 font-medium text-right">Rows</th>
            <th className="py-1 pr-2 font-medium text-right">Attributed</th>
            <th className="py-1 font-medium">Writer</th>
          </tr>
        </thead>
        <tbody>
          {cov.tables.map((t) => (
            <tr key={t.table} className="border-t border-gray-100 align-top">
              <td className="py-1 pr-2 text-gray-900">{label(t.table)}</td>
              <td className="py-1 pr-2 text-right font-mono text-gray-700">{t.rows}</td>
              <td className="py-1 pr-2 text-right font-mono text-gray-700">
                {/* NO ROWS RENDERS AS "—", never as a percentage. An empty window
                    is neither attributed nor unattributed, and both numbers would
                    be read as a verdict. */}
                {t.percent === null
                  ? <span title="no rows in this window, so there is nothing to attribute">—</span>
                  : <span title={`${t.attributed} of ${t.rows}`}>{t.percent}%</span>}
              </td>
              <td className="py-1 text-gray-600">
                {t.writer === 'live'
                  ? <span className="text-emerald-700">live</span>
                  : (
                    <span className="text-gray-500" title={t.note || undefined}>
                      none — {t.note ? 'why ⓘ' : 'no writer'}
                    </span>
                  )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* THE UNATTRIBUTED COUNT, STATED. A model-vs-model comparison over this
          window is only as good as the share of it that carries a model, and a
          reader who cannot see that number will treat a small attributed sample
          as the whole story. */}
      <p className="mt-2 text-[11px] text-gray-700">
        {cov.unattributed_attempts > 0
          ? <>
              <span className="font-medium">{cov.unattributed_attempts}</span> authoring
              attempt{cov.unattributed_attempts === 1 ? '' : 's'} in this window carry no
              model — a model-vs-model comparison cannot see {cov.unattributed_attempts === 1 ? 'it' : 'them'}.
            </>
          : <>Every authoring attempt in this window carries a model.</>}
      </p>

      {cov.by_model.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600">
          {cov.by_model.map((m) => (
            <li key={`${m.provider}/${m.model}`}>
              <span className="font-mono">{m.provider}/{m.model}</span>
              {' · '}{m.rows} attempt{m.rows === 1 ? '' : 's'}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
